// client/src/coms/components/mobile/MobileHeader.js
// Phones: the one header above every screen. It always names where you are (the channel and its server, the
// person in a DM, or the area), offers one context button on the left (the server drawer, or back) and a few
// actions on the right. Navigation itself lives in the bar, menu or gestures chosen in Mobile Layout, never here
// as a second copy.
import React, { useEffect, useState } from 'react';
import { comsApiGet } from '../../comsApi';
import NavIcon from './NavIcon';
import { ServerBannerArt, bannerShowsAt, useServerBranding } from '../ServerBanner';
import './mobile.css';

function initials(s) {
  const parts = String(s || '?').trim().split(/\s+/).filter(Boolean);
  return (parts.length > 1 ? parts[0][0] + parts[1][0] : String(s || '?').slice(0, 2)).toUpperCase();
}

// A server's logo: from the server object when it carries one, otherwise looked up once.
export function useServerLogo(server) {
  const direct = String((server && (server.logo_url || server.logoUrl || server.server_logo_url || server.serverLogoUrl)) || '').trim();
  const sid = server && server.id != null ? String(server.id).trim() : '';
  const [fetched, setFetched] = useState({ sid: '', url: '' });
  useEffect(() => {
    if (direct || !sid || fetched.sid === sid) return undefined;
    let alive = true;
    comsApiGet(`/server-logo/${encodeURIComponent(sid)}`)
      .then((r) => alive && setFetched({ sid, url: String((r && r.logo_url) || '').trim() }))
      .catch(() => alive && setFetched({ sid, url: '' }));
    return () => {
      alive = false;
    };
  }, [direct, sid, fetched.sid]);
  return direct || (fetched.sid === sid ? fetched.url : '');
}

function Picture({ src, label, round }) {
  const [broken, setBroken] = useState(false);
  return (
    <span className={`vx-mh-pic${round ? ' is-round' : ''}`} aria-hidden="true">
      <span>{initials(label)}</span>
      {src && !broken ? <img src={src} alt="" onError={() => setBroken(true)} /> : null}
    </span>
  );
}

export default function MobileHeader({
  left = null, // { kind: 'server' | 'back', label, logo, onClick }
  title,
  subtitle = '',
  titlePrefix = '', // '#' for channels, '🔊' for voice
  avatar = null, // { src, label } for a DM partner
  menu = null, // { open, onToggle, label }: the title opens the navigation menu (Dropdown layouts)
  actions = [], // [{ id, icon, label, onClick, pressed }]
  size = 'regular',
  children = null, // a navigation row placed under the title row (Top navigation)
  bannerServer = '', // server key: its banner sits behind the header while one of its channels is open
}) {
  const branding = useServerBranding(bannerServer);
  // Phones adapt "Top of server" to the header itself (any size), so chat and navigation keep their space. The
  // sidebar and overview positions show in the channel list and the overview sheet instead.
  const showBanner = !!bannerServer && branding.loaded && bannerShowsAt(branding.display, 'top');
  const titleInner = (
    <>
      {avatar ? <Picture src={avatar.src} label={avatar.label} round /> : null}
      <span className="vx-mh-text">
        <span className="vx-mh-title">
          {titlePrefix ? <span className="vx-mh-prefix">{titlePrefix}</span> : null}
          <span className="vx-mh-name">{title}</span>
          {menu ? (
            <span className={`vx-mh-caret${menu.open ? ' is-open' : ''}`} aria-hidden="true">
              <NavIcon id="down" size={16} />
            </span>
          ) : null}
        </span>
        {subtitle && size !== 'slim' ? <span className="vx-mh-sub">{subtitle}</span> : null}
      </span>
      {subtitle && size === 'slim' ? <span className="vx-mh-sub is-inline">{subtitle}</span> : null}
    </>
  );

  return (
    <header className={`vx-mh is-${size}${showBanner ? ' has-banner' : ''}`} data-testid="mobile-header">
      {showBanner ? (
        <span className="vx-mh-banner" aria-hidden="true">
          <ServerBannerArt seed={bannerServer} banner={branding.banner} shape="wide" />
        </span>
      ) : null}
      <div className="vx-mh-row">
        {left ? (
          <button type="button" className={`vx-mh-left is-${left.kind}`} onClick={left.onClick} aria-label={left.label} title={left.label} data-testid={`mobile-header-${left.kind}`}>
            {left.kind === 'back' ? <NavIcon id="back" size={24} /> : <Picture src={left.logo} label={left.name || left.label} />}
          </button>
        ) : null}

        {menu ? (
          <button type="button" className="vx-mh-id is-menu" onClick={menu.onToggle} aria-expanded={!!menu.open} aria-haspopup="menu" aria-label={menu.label} data-testid="mobile-menu-toggle">
            {titleInner}
          </button>
        ) : (
          <div className="vx-mh-id" data-testid="mobile-header-title">
            {titleInner}
          </div>
        )}

        {actions.map((a) => (
          <button
            key={a.id}
            type="button"
            className={`vx-mh-act${a.pressed ? ' is-on' : ''}`}
            onClick={a.onClick}
            aria-label={a.label}
            title={a.label}
            aria-pressed={a.pressed == null ? undefined : !!a.pressed}
            data-testid={`mobile-header-${a.id}`}
          >
            <span aria-hidden="true">{a.icon}</span>
          </button>
        ))}
      </div>
      {children}
    </header>
  );
}
